import React, { useState, useEffect, useRef } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Database, MessageSquare, ChevronDown, MapPin } from 'lucide-react';
import { log } from '../components/DebugLogger';
import { useProfileStore } from '../lib/supabase';
import { useCollectionStore } from '../lib/collectionStore';
import { countries, detectUserCountry, type Country } from '../data/countries';
import FlagIcon from './FlagIcon';
import Logo from './Logo';

// Get the site URL for redirects
const getSiteUrl = () => import.meta.env.PROD ? 'https://pokecardindex.com' : window.location.origin;

export default function Auth() {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 2000;
  const [username, setUsername] = useState(() => {
    // Initialize with email prefix if coming back from verification
    const searchParams = new URLSearchParams(window.location.search);
    const email = searchParams.get('email');
    return email ? email.split('@')[0] : '';
  });
  const [isSignUp, setIsSignUp] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.has('register');
  });
  const [error, setError] = useState<string | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const [networkError, setNetworkError] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    email?: string;
    password?: string;
    username?: string;
    country?: string;
  }>({});
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState<string | null>(null);
  const [countryDetectionLoading, setCountryDetectionLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Clear any existing sessions on mount
  useEffect(() => {
    if (!isSupabaseConfigured()) {
      log('Supabase not configured', 'error');
      setError('Authentication is not available at this time. Please try again later.');
      setLoading(false);
      return;
    }
    
    log('Initializing auth component');
    
    // Update isSignUp when URL changes
    const searchParams = new URLSearchParams(location.search);
    setIsSignUp(searchParams.has('register'));

    // Clear any existing sessions
    supabase.auth.signOut().then(() => {
      log('Cleared existing session');
    }).catch(error => {
      log('Error clearing session: ' + error.message, 'error');
    });

    // Auto-detect country for sign-up
    if (searchParams.has('register') && !detectedCountry) {
      setCountryDetectionLoading(true);
      detectUserCountry().then(countryCode => {
        if (countryCode) {
          setDetectedCountry(countryCode);
          setSelectedCountry(countryCode);
        }
      }).finally(() => {
        setCountryDetectionLoading(false);
      });
    }
  }, [location.search, detectedCountry]);

  // Keyboard navigation for country dropdown
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!countryDropdownOpen) return;
      
      if (e.key === 'Escape') {
        setCountryDropdownOpen(false);
        setHighlightedIndex(-1);
        return;
      }
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < countries.length - 1 ? prev + 1 : 0
        );
        return;
      }
      
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : countries.length - 1
        );
        return;
      }
      
      if (e.key === 'Enter' && highlightedIndex >= 0) {
        e.preventDefault();
        const country = countries[highlightedIndex];
        setSelectedCountry(country.code);
        setCountryDropdownOpen(false);
        setHighlightedIndex(-1);
        setValidationErrors(prev => ({ ...prev, country: undefined }));
        return;
      }
      
      // Jump to letter
      if (/^[a-zA-Z]$/.test(e.key)) {
        const letter = e.key.toLowerCase();
        const index = countries.findIndex(country => 
          country.name.toLowerCase().startsWith(letter)
        );
        if (index !== -1) {
          setHighlightedIndex(index);
          // Scroll to highlighted item
          setTimeout(() => {
            const dropdown = dropdownRef.current;
            const highlightedElement = dropdown?.children[index + 1] as HTMLElement; // +1 for hint div
            if (highlightedElement && dropdown) {
              const dropdownRect = dropdown.getBoundingClientRect();
              const elementRect = highlightedElement.getBoundingClientRect();
              
              if (elementRect.bottom > dropdownRect.bottom) {
                dropdown.scrollTop += elementRect.bottom - dropdownRect.bottom;
              } else if (elementRect.top < dropdownRect.top) {
                dropdown.scrollTop -= dropdownRect.top - elementRect.top;
              }
            }
          }, 0);
        }
      }
    };

    if (countryDropdownOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [countryDropdownOpen, highlightedIndex]);

  // Scroll to highlighted item when changed
  useEffect(() => {
    if (countryDropdownOpen && highlightedIndex >= 0) {
      const dropdown = dropdownRef.current;
      const highlightedElement = dropdown?.children[highlightedIndex + 1] as HTMLElement; // +1 for hint div
      if (highlightedElement && dropdown) {
        const dropdownRect = dropdown.getBoundingClientRect();
        const elementRect = highlightedElement.getBoundingClientRect();
        
        if (elementRect.bottom > dropdownRect.bottom) {
          dropdown.scrollTop += elementRect.bottom - dropdownRect.bottom;
        } else if (elementRect.top < dropdownRect.top) {
          dropdown.scrollTop -= dropdownRect.top - elementRect.top;
        }
      }
    }
  }, [highlightedIndex, countryDropdownOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isSupabaseConfigured()) {
      setError('Authentication is not available at this time. Please try again later.');
      setLoading(false);
      return;
    }

    // Reset retry count on new submission
    setRetryCount(0);

    setError(null);
    setValidationErrors({});
    setLoading(true);

    const errors: typeof validationErrors = {};
    
    if (!email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    if (isSignUp && !username) {
      errors.username = 'Username is required';
    }

    if (isSignUp && !selectedCountry) {
      errors.country = 'Please select your country';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setLoading(false);
      return;
    }

    const attemptAuth = async () => {
      try {
        if (isSignUp) {
          console.log('Attempting to sign up user...');
          if (!isSupabaseConfigured()) {
            throw new Error('Authentication is not available at this time. Please try again later.');
          }

          const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
              data: {
                username,
                country_code: selectedCountry,
                country: countries.find(c => c.code === selectedCountry)?.name || selectedCountry,
              },
              emailRedirectTo: `${getSiteUrl()}/auth/callback`,
            },
          });

          if (error) {
            console.error('Sign up error:', error);
            throw error;
          }

          if (data.user) {
            console.log('User created successfully');
            setVerificationSent(true);
          }
        } else {
          console.log('Attempting to sign in user...');
          if (!isSupabaseConfigured()) {
            throw new Error('Authentication is not available at this time. Please try again later.');
          }

          const { error } = await supabase.auth.signInWithPassword({
            email,
            password,
          });
          
          if (error) {
            console.error('Sign in error:', error);
            throw error;
          }
          
          navigate('/');
        }
      } catch (error) {
        console.error('Auth error:', error);
        const message = error instanceof Error ? error.message : 'An error occurred';
        
        // Handle specific error cases
        if (message.includes('Email not confirmed')) {
          setVerificationSent(true);
        } else if (message.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please try again.');
        } else if (message.includes('User already registered')) {
          setError('An account with this email already exists');
        } else if (message.includes('Failed to fetch') || message.includes('Network Error')) {
          // Retry on network errors
          if (retryCount < MAX_RETRIES) {
            console.log(`Network error, retrying... Attempt ${retryCount + 1} of ${MAX_RETRIES}`);
            setRetryCount(prev => prev + 1);
            setTimeout(attemptAuth, RETRY_DELAY);
            return;
          }
          setNetworkError(true);
          setError('Unable to connect to the authentication service. Please check your internet connection and try again.');
        } else if (message.includes('Database error')) {
          setError('Service is temporarily unavailable. Please try again in a few minutes.');
          console.error('Database error during auth:', message);
        } else {
          setError(message);
        }
        setLoading(false);
      }
    };

    attemptAuth();
  };

  const handleSSOSignIn = async (provider: 'google' | 'discord') => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`Starting ${provider} OAuth sign in`);

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: provider,
        options: {
          redirectTo: `${getSiteUrl()}/auth/callback`,
          scopes: 'identify email guilds',
          queryParams: {
            prompt: 'consent'
          }
        }
      });

      if (error) {
        console.error('OAuth error:', error);
        throw error;
      }

      // Log the OAuth URL
      if (data?.url) {
        console.log('Redirecting to OAuth URL:', data.url);
        window.location.href = data.url;
      } else {
        console.error('No OAuth URL returned');
        throw new Error('No OAuth URL returned');
      }
    } catch (error) {
      console.error('SSO error:', error);
      setError(error instanceof Error ? error.message : 'An error occurred');
      setLoading(false);
    }
  };

  // Show network error message
  if (networkError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="bg-white/5 p-8 rounded-lg shadow-xl border border-white/10 w-full max-w-md text-center">
          <h2 className="text-2xl font-bold mb-4 text-white">Connection Error</h2>
          <p className="text-white/60 mb-6">
            Unable to connect to the authentication service. Please check your internet connection and try again.
          </p>
          <button
            onClick={() => {
              setNetworkError(false);
              setError(null);
              setLoading(false);
            }}
            className="w-full py-2 px-4 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (verificationSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="bg-white/5 p-8 rounded-lg shadow-xl border border-white/10 w-full max-w-md text-center">
          <h2 className="text-2xl font-bold mb-4 text-white">Verify Your Email</h2>
          <p className="text-white/60 mb-6">
            We've sent a verification link to <strong className="text-white">{email}</strong>.
            Please check your email and click the link to verify your account.
          </p>
          <button
            onClick={() => setVerificationSent(false)}
            className="w-full py-2 px-4 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition-colors"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-black">
      {/* Logo Header */}
      <div className="p-4">
        <Link to="/" className="inline-flex items-center text-white">
          <Logo className="h-10 w-auto" />
        </Link>
      </div>

      {/* Auth Form */}
      <div className="flex-1 flex items-center justify-center">
        <div className="bg-white/5 p-8 rounded-lg shadow-xl border border-white/10 w-full max-w-md">
          <h2 className="text-2xl font-bold mb-6 text-white text-center">
            {isSignUp ? 'Create Account' : 'Sign In'}
          </h2>
          
          {/* SSO Buttons */}
          <div className="space-y-3 mb-6">
            <button
              onClick={() => handleSSOSignIn('discord')}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 px-4 py-2 bg-[#5865F2]/20 hover:bg-[#5865F2]/30 text-white transition-colors rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MessageSquare size={20} />
              Continue with Discord
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-black text-white/60">Or continue with email</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isSignUp && (
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-white/60 mb-1">
                  Username
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={`w-full px-4 py-2 rounded-lg bg-white/5 border ${
                    validationErrors.username ? 'border-red-500' : 'border-white/10'
                  } text-white focus:outline-none focus:ring-2 focus:ring-white/25`}
                  placeholder="Choose a username"
                  required
                />
                {validationErrors.username && (
                  <p className="mt-1 text-sm text-red-500">{validationErrors.username}</p>
                )}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-white/60 mb-1">
                Email
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full px-4 py-2 rounded-lg bg-white/5 border ${
                  validationErrors.email ? 'border-red-500' : 'border-white/10'
                } text-white focus:outline-none focus:ring-2 focus:ring-white/25`}
                required
              />
              {validationErrors.email && (
                <p className="mt-1 text-sm text-red-500">{validationErrors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-white/60 mb-1">
                Password
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full px-4 py-2 rounded-lg bg-white/5 border ${
                  validationErrors.password ? 'border-red-500' : 'border-white/10'
                } text-white focus:outline-none focus:ring-2 focus:ring-white/25`}
                required
              />
              {validationErrors.password && (
                <p className="mt-1 text-sm text-red-500">{validationErrors.password}</p>
              )}
            </div>

            {/* Country Selection - Only for Sign Up */}
            {isSignUp && (
              <div className="relative">
                <label htmlFor="country" className="block text-sm font-medium text-white/60 mb-1">
                  Country
                  <span className="text-red-500 ml-1">*</span>
                  {countryDetectionLoading && (
                    <span className="ml-2 text-xs text-white/40">Detecting location...</span>
                  )}
                  {detectedCountry && !countryDetectionLoading && (
                    <span className="ml-2 text-xs text-green-400 flex items-center gap-1">
                      <MapPin size={12} />
                      Auto-detected
                    </span>
                  )}
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setCountryDropdownOpen(!countryDropdownOpen);
                      setHighlightedIndex(-1);
                    }}
                    className={`w-full px-4 py-2 rounded-lg bg-white/5 border ${
                      validationErrors.country ? 'border-red-500' : 'border-white/10'
                    } text-white focus:outline-none focus:ring-2 focus:ring-white/25 flex items-center justify-between`}
                  >
                    <span className="flex items-center gap-2">
                      {selectedCountry ? (
                        <>
                          <FlagIcon countryCode={selectedCountry} size="sm" />
                          <span>{countries.find(c => c.code === selectedCountry)?.name}</span>
                        </>
                      ) : (
                        <span className="text-white/60">Select your country</span>
                      )}
                    </span>
                    <ChevronDown 
                      size={16} 
                      className={`transform transition-transform ${countryDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  
                  {countryDropdownOpen && (
                    <div 
                      ref={dropdownRef}
                      className="absolute top-full left-0 right-0 mt-1 bg-black/90 border border-white/10 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto"
                    >
                      {/* Search hint */}
                      <div className="px-3 py-2 border-b border-white/10 text-xs text-white/60 bg-white/5">
                        💡 Press any letter to jump to countries • Use ↑↓ arrow keys • Enter to select • Esc to close
                      </div>
                      
                      {countries.map((country, index) => (
                        <button
                          key={country.code}
                          type="button"
                          onClick={() => {
                            setSelectedCountry(country.code);
                            setCountryDropdownOpen(false);
                            setHighlightedIndex(-1);
                            setValidationErrors(prev => ({ ...prev, country: undefined }));
                          }}
                          className={`w-full px-4 py-2 text-left flex items-center gap-3 text-white transition-colors border-b border-white/5 last:border-b-0 ${
                            index === highlightedIndex 
                              ? 'bg-white/20' 
                              : 'hover:bg-white/10'
                          }`}
                        >
                          <FlagIcon countryCode={country.code} size="sm" />
                          <span className="text-sm font-medium">{country.name}</span>
                          <span className="text-xs text-white/40 ml-auto">{country.code}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {validationErrors.country && (
                  <p className="mt-1 text-sm text-red-500">{validationErrors.country}</p>
                )}
                <p className="mt-1 text-xs text-white/40">
                  This helps us apply appropriate regional rules and pricing
                </p>
              </div>
            )}

            {!isSignUp && (
              <button
                type="button"
                onClick={() => navigate('/auth/reset-password')}
                className="w-full text-white/60 hover:text-white text-sm transition-colors mt-2"
              >
                Forgot password?
              </button>
            )}

            {error && (
              <div className="text-red-500 text-sm bg-red-500/10 p-3 rounded-lg">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 px-4 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : isSignUp ? 'Create Account' : 'Sign In'}
            </button>

            <button
              type="button"
              onClick={() => setIsSignUp(!isSignUp)}
              className="w-full text-white/60 hover:text-white text-sm transition-colors"
            >
              {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

const handleLogout = async () => {
  await supabase.auth.signOut();
  useProfileStore.getState().reset();
  useCollectionStore.getState().reset();
  navigate('/');
  onClose();
};